import { AccessTimeOutlined, GpsFixedOutlined, LocationOnOutlined } from "@mui/icons-material";
import { Box, Chip, Divider, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { MarkerF, PolylineF } from "../../../../../theme/maps";
import { TimesheetRow } from "../../../interfaces/time-sheet.interfaces";

interface Props {
  row: TimesheetRow;
}

const PUNCH_IN_COLOR = "#ea580c";
const PUNCH_IN_BG = "#fff7ed";
const PUNCH_IN_BORDER = "#ffedd5";
const PUNCH_OUT_COLOR = "#dc2626";
const PUNCH_OUT_BG = "#fef2f2";
const PUNCH_OUT_BORDER = "#fee2e2";

const formatDate = (value?: string | null, format = "hh:mm a") => {
  if (!value) return "-";
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toFormat(format) : "-";
};

const toCoordinate = (value?: number | string | null): number | null => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const reverseGeocode = async (latitude: number, longitude: number): Promise<string | null> => {
  try {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      return null;
    }

    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`);
    const data = await response.json();

    if (data?.status === "OK" && data?.results?.length > 0) {
      return data.results[0].formatted_address ?? null;
    }

    return null;
  } catch (error) {
    console.error("Reverse geocoding error:", error);

    return null;
  }
};

interface LocationCardProps {
  type: "in" | "out";
  time?: string | null;
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  accuracy?: number | string | null;
  loading: boolean;
  selected?: boolean;
  onClick?: () => void;
}

function LocationCard({ type, time, latitude, longitude, address, accuracy, loading, selected = false, onClick }: LocationCardProps) {
  const isPunchIn = type === "in";
  const hasLocation = latitude !== null && longitude !== null;
  const mainColor = isPunchIn ? PUNCH_IN_COLOR : PUNCH_OUT_COLOR;
  const bgColor = isPunchIn ? PUNCH_IN_BG : PUNCH_OUT_BG;
  const borderColor = isPunchIn ? PUNCH_IN_BORDER : PUNCH_OUT_BORDER;

  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        border: "1px solid",
        borderColor: selected ? mainColor : "#e2e8f0",
        borderRadius: 2.5,
        bgcolor: selected ? bgColor : "var(--fx-surface-base, #FFFFFF)",
        boxShadow: selected ? `0 0 0 2px ${mainColor}20` : "0 1px 3px rgba(0,0,0,0.03)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s ease",
        "&:hover": onClick
          ? {
              borderColor: mainColor,
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }
          : {},
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: bgColor,
              border: `1px solid ${borderColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LocationOnOutlined
              sx={{
                fontSize: 18,
                color: hasLocation ? mainColor : "#cbd5e1",
              }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--fx-surface-text, #0F172A)",
            }}
          >
            {isPunchIn ? "Arrival" : "Departure"}
          </Typography>
        </Box>

        <Chip
          icon={
            <AccessTimeOutlined
              sx={{
                fontSize: "14px !important",
                color: `${mainColor} !important`,
              }}
            />
          }
          label={formatDate(time)}
          size="small"
          sx={{
            bgcolor: bgColor,
            color: mainColor,
            fontWeight: 700,
            fontSize: 12,
            border: `1px solid ${borderColor}`,
            height: 26,
          }}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: "var(--fx-surface-texttertiary, #94A3B8)",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            mb: 0.5,
          }}
        >
          Address
        </Typography>

        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 500,
            color: "var(--fx-surface-textsecondary, #64748B)",
            lineHeight: 1.4,
            minHeight: 36,
          }}
        >
          {loading ? "Loading address..." : address || "Address not available"}
        </Typography>
      </Box>
    </Box>
  );
}

interface PunchMapProps {
  startLatitude: number | null;
  startLongitude: number | null;
  endLatitude: number | null;
  endLongitude: number | null;
  selectedPoint: "in" | "out" | null;
  onClearSelection: () => void;
}

function PunchMap({ startLatitude, startLongitude, endLatitude, endLongitude, selectedPoint, onClearSelection }: PunchMapProps) {
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const hasStart = startLatitude !== null && startLongitude !== null;
  const hasEnd = endLatitude !== null && endLongitude !== null;

  const startPosition = hasStart
    ? {
        lat: startLatitude!,
        lng: startLongitude!,
      }
    : null;

  const endPosition = hasEnd
    ? {
        lat: endLatitude!,
        lng: endLongitude!,
      }
    : null;

  const fitBothLocations = () => {
    if (!mapRef.current || !startPosition || !endPosition) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(startPosition);
    bounds.extend(endPosition);
    mapRef.current.fitBounds(bounds, 60);

    google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
      if (!mapRef.current) {
        return;
      }

      const zoom = mapRef.current.getZoom();

      if (zoom !== undefined && zoom < 15) {
        mapRef.current.setZoom(15);
      }
    });
  };

  useEffect(() => {
    if (!mapRef.current || !selectedPoint) {
      return;
    }

    let position: google.maps.LatLngLiteral | null = null;

    if (selectedPoint === "in" && startPosition) {
      position = startPosition;
    }

    if (selectedPoint === "out" && endPosition) {
      position = endPosition;
    }

    if (!position) {
      return;
    }

    mapRef.current.panTo(position);

    mapRef.current.setZoom(16);
  }, [selectedPoint, startLatitude, startLongitude, endLatitude, endLongitude]);

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;

    if (startPosition && endPosition) {
      const bounds = new google.maps.LatLngBounds();

      bounds.extend(startPosition);
      bounds.extend(endPosition);
      map.fitBounds(bounds, 60);

      google.maps.event.addListenerOnce(map, "idle", () => {
        const zoom = map.getZoom();

        if (zoom !== undefined && zoom < 15) {
          map.setZoom(15);
        }
      });

      return;
    }

    if (startPosition) {
      map.setCenter(startPosition);
      map.setZoom(16);
      return;
    }

    if (endPosition) {
      map.setCenter(endPosition);
      map.setZoom(16);
    }
  };

  const handleMapUnmount = () => {
    mapRef.current = null;
  };

  if (loadError) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "#dc2626",
            textAlign: "center",
          }}
        >
          Unable to load Google Maps. Please check your API key.
        </Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--fx-surface-texttertiary, #94A3B8)",
          }}
        >
          Loading map...
        </Typography>
      </Box>
    );
  }

  const defaultCenter = startPosition ||
    endPosition || {
      lat: 20.9674,
      lng: -89.5926,
    };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      <GoogleMap
        mapContainerStyle={{
          width: "100%",
          height: "100%",
        }}
        center={defaultCenter}
        zoom={15}
        onLoad={handleMapLoad}
        onUnmount={handleMapUnmount}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          zoomControl: true,
          gestureHandling: "greedy",
        }}
      >
        {startPosition && (
          <MarkerF
            position={startPosition}
            label={{
              text: "A",
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: "700",
            }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: selectedPoint === "in" ? 13 : 10,
              fillColor: PUNCH_IN_COLOR,
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 3,
            }}
            title="Arrival"
            zIndex={selectedPoint === "in" ? 100 : 10}
          />
        )}

        {endPosition && (
          <MarkerF
            position={endPosition}
            label={{
              text: "B",
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: "700",
            }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: selectedPoint === "out" ? 13 : 10,
              fillColor: PUNCH_OUT_COLOR,
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 3,
            }}
            title="Departure"
            zIndex={selectedPoint === "out" ? 100 : 10}
          />
        )}
      </GoogleMap>

      {selectedPoint !== null && startPosition && endPosition && (
        <Box
          component="button"
          onClick={() => {
            onClearSelection();

            setTimeout(() => {
              fitBothLocations();
            }, 50);
          }}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.5,
            py: 0.9,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 12,
            fontWeight: 700,
            color: "var(--fx-surface-textsecondary, #64748B)",
            transition: "all 0.2s ease",

            "&:hover": {
              bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
              borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
              transform: "translateY(-1px)",
            },
          }}
        >
          {/* A */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: PUNCH_IN_COLOR,
            }}
          />
          {/* B */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: PUNCH_OUT_COLOR,
            }}
          />
          View both locations
        </Box>
      )}
    </Box>
  );
}

export default function PunchLocationModal({ row }: Props) {
  const startLatitude = toCoordinate(row.startLatitude);
  const startLongitude = toCoordinate(row.startLongitude);
  const endLatitude = toCoordinate(row.endLatitude);
  const endLongitude = toCoordinate(row.endLongitude);
  const hasPunchInLocation = startLatitude !== null && startLongitude !== null;
  const hasPunchOutLocation = endLatitude !== null && endLongitude !== null;
  const hasAnyLocation = hasPunchInLocation || hasPunchOutLocation;
  const [startAddress, setStartAddress] = useState<string | null>(null);
  const [endAddress, setEndAddress] = useState<string | null>(null);
  const [isLoadingStartAddress, setIsLoadingStartAddress] = useState(false);
  const [isLoadingEndAddress, setIsLoadingEndAddress] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState<"in" | "out" | null>(null);

  useEffect(() => {
    if (!hasPunchInLocation || startLatitude === null || startLongitude === null) {
      setStartAddress(null);

      return;
    }

    let cancelled = false;

    const loadAddress = async () => {
      setIsLoadingStartAddress(true);

      const address = await reverseGeocode(startLatitude, startLongitude);

      if (!cancelled) {
        setStartAddress(address);

        setIsLoadingStartAddress(false);
      }
    };

    loadAddress();

    return () => {
      cancelled = true;
    };
  }, [startLatitude, startLongitude, hasPunchInLocation]);

  useEffect(() => {
    if (!hasPunchOutLocation || endLatitude === null || endLongitude === null) {
      setEndAddress(null);

      return;
    }

    let cancelled = false;

    const loadAddress = async () => {
      setIsLoadingEndAddress(true);

      const address = await reverseGeocode(endLatitude, endLongitude);

      if (!cancelled) {
        setEndAddress(address);

        setIsLoadingEndAddress(false);
      }
    };

    loadAddress();

    return () => {
      cancelled = true;
    };
  }, [endLatitude, endLongitude, hasPunchOutLocation]);
  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "fit-content",
          maxWidth: "100%",
          maxHeight: "100%",
          minWidth: "480px",
          display: "flex",
          flexDirection: "column",

          p: 2,
          gap: 2,
        }}
      >
        <Box
          sx={{
            height: 280,
            width: "100%",
            borderRadius: 2.5,
            overflow: "hidden",
            bgcolor: "var(--fx-surface-muted, #F1F5F9)",
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            position: "relative",
            flexShrink: 0,
          }}
        >
          {hasAnyLocation ? (
            <>
              <PunchMap
                startLatitude={startLatitude}
                startLongitude={startLongitude}
                endLatitude={endLatitude}
                endLongitude={endLongitude}
                selectedPoint={selectedPoint}
                onClearSelection={() => {
                  setSelectedPoint(null);
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  width: "fit-content",
                  maxWidth: "calc(100% - 24px)",
                  height: "fit-content",
                  px: 1.25,
                  py: 0.75,
                  bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.95)",
                  backdropFilter: "blur(4px)",
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  border: "1px solid rgba(var(--fx-surface-basergb, 255, 255, 255), 0.8)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  pointerEvents: "none",
                  background: "var(--fx-surface-base, #FFFFFF)",
                }}
              >
                {hasPunchInLocation && (
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: PUNCH_IN_COLOR,
                        flexShrink: 0,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "var(--fx-surface-textsecondary, #64748B)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      A: Arrival
                    </Typography>
                  </Box>
                )}

                {hasPunchOutLocation && (
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: PUNCH_OUT_COLOR,
                        flexShrink: 0,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "var(--fx-surface-textsecondary, #64748B)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      B: Departure
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--fx-surface-texttertiary, #94A3B8)",
                }}
              >
                No punch location available
              </Typography>
            </Box>
          )}
        </Box>

        {hasPunchInLocation && hasPunchOutLocation && (
          <Typography
            sx={{
              fontSize: 11,
              color: "var(--fx-surface-texttertiary, #94A3B8)",
              textAlign: "center",
              mt: -0.5,
              mb: -0.5,
            }}
          >
            Click Arrival or Departure to focus on that location.
          </Typography>
        )}

        <Box
          display="flex"
          gap={1.5}
          width="100%"
          sx={{
            flexShrink: 0,
          }}
        >
          {hasPunchInLocation && (
            <LocationCard
              type="in"
              time={row.startAt}
              latitude={startLatitude}
              longitude={startLongitude}
              address={startAddress}
              accuracy={row.startLocationAccuracy}
              loading={isLoadingStartAddress}
              selected={selectedPoint === "in"}
              onClick={() => {
                setSelectedPoint("in");
              }}
            />
          )}

          {hasPunchOutLocation && (
            <LocationCard
              type="out"
              time={row.endAt}
              latitude={endLatitude}
              longitude={endLongitude}
              address={endAddress}
              accuracy={row.endLocationAccuracy}
              loading={isLoadingEndAddress}
              selected={selectedPoint === "out"}
              onClick={() => {
                setSelectedPoint("out");
              }}
            />
          )}
        </Box>
      </Box>
    </>
  );
}
